import type { MeDto, PresetSummaryDto, PresetVersionDto, ProjectSummaryDto } from '@scs/schemas';
import type { PresetContent } from '@scs/domain-engine';

let csrfToken: string | null = null;

export function setCsrf(token: string | null) {
  csrfToken = token;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly body: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

/** Network failures surface as status 0 so callers can show "Offline" instead of a server error. */
export async function api<T>(method: string, url: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET' && csrfToken) headers['x-csrf-token'] = csrfToken;
  let res: Response;
  try {
    res = await fetch(url, { method, headers, credentials: 'same-origin', body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new ApiError(0, 'network', 'Nema veze sa serverom.');
  }
  const text = await res.text();
  const json = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  if (!res.ok) throw new ApiError(res.status, String(json.error ?? 'error'), String(json.message ?? res.statusText), json);
  return json as T;
}

export const Api = {
  me: () => api<MeDto>('GET', '/api/auth/me'),
  login: (email: string, password: string) => api<MeDto>('POST', '/api/auth/login', { email, password }),
  authConfig: () => api<{ registration: boolean }>('GET', '/api/auth/config'),
  register: (body: { email: string; name: string; password: string; workspaceName: string }) => api<MeDto>('POST', '/api/auth/register', body),
  adminSettings: () => api<{ publicRegistration: boolean; mail: string }>('GET', '/api/admin/settings'),
  setAdminSettings: (publicRegistration: boolean) => api<{ publicRegistration: boolean }>('PUT', '/api/admin/settings', { publicRegistration }),
  members: (wsId: string) => api<{ userId: string; email: string; name: string; role: string; since: string }[]>('GET', `/api/workspaces/${wsId}/members`),
  invite: (wsId: string, email: string, role: string) => api<{ id: string; mailSent: boolean; link: string }>('POST', `/api/workspaces/${wsId}/invitations`, { email, role }),
  setRole: (wsId: string, userId: string, role: string) => api('PATCH', `/api/workspaces/${wsId}/members/${userId}`, { role }),
  removeMember: (wsId: string, userId: string) => api('DELETE', `/api/workspaces/${wsId}/members/${userId}`),
  logout: () => api('POST', '/api/auth/logout'),
  changePassword: (currentPassword: string, newPassword: string) => api('POST', '/api/auth/change-password', { currentPassword, newPassword }),
  resetRequest: (email: string) => api<{ message: string }>('POST', '/api/auth/reset/request', { email }),
  resetConfirm: (token: string, newPassword: string) => api('POST', '/api/auth/reset/confirm', { token, newPassword }),
  acceptInvite: (token: string, name: string, password: string) => api('POST', '/api/auth/accept-invite', { token, name, password }),
  switchWorkspace: (workspaceId: string) => api('POST', '/api/workspaces/switch', { workspaceId }),
  projects: (view: 'active' | 'archived' | 'trash', q: string) =>
    api<ProjectSummaryDto[]>('GET', `/api/projects?view=${view}${q ? `&q=${encodeURIComponent(q)}` : ''}`),
  createProject: (name: string, client: string, document?: unknown) => api<ProjectSummaryDto>('POST', '/api/projects', { name, client, document }),
  patchProject: (id: string, patch: Partial<Pick<ProjectSummaryDto, 'name' | 'client' | 'archived' | 'status'>>) =>
    api<ProjectSummaryDto>('PATCH', `/api/projects/${id}`, patch),
  deleteProject: (id: string) => api('DELETE', `/api/projects/${id}`),
  restoreProject: (id: string) => api('POST', `/api/projects/${id}/restore`),
  duplicateProject: (id: string) => api<ProjectSummaryDto>('POST', `/api/projects/${id}/duplicate`),
  project: (id: string) =>
    api<{ project: ProjectSummaryDto; role: string; revision: number; document: unknown; updatedAt: string }>('GET', `/api/projects/${id}`),
  saveDocument: (id: string, baseRevision: number, document: unknown) =>
    api<{ revision: number; savedAt: string; validation: { errors: number; warnings: number } }>('PUT', `/api/projects/${id}/document`, { baseRevision, document }),
  revisions: (id: string) =>
    api<{ revisions: { revision: number; message: string | null; createdAt: string }[]; versions: { id: string; revision: number; name: string }[] }>('GET', `/api/projects/${id}/revisions`),
  restoreRevision: (id: string, rev: number) => api<{ revision: number }>('POST', `/api/projects/${id}/revisions/${rev}/restore`),
  nameVersion: (id: string, revision: number, name: string) => api('POST', `/api/projects/${id}/versions`, { revision, name }),
  presets: () => api<PresetSummaryDto[]>('GET', '/api/presets'),
  preset: (id: string) =>
    api<PresetSummaryDto & { versions: { id: string; version: number; message: string | null; createdAt: string }[] }>('GET', `/api/presets/${id}`),
  presetVersion: (versionId: string) => api<PresetVersionDto>('GET', `/api/preset-versions/${versionId}`),
  createPreset: (body: { name: string; category: string; folder?: string; scope: 'workspace' | 'private'; description: string; tags: string[]; content: PresetContent; thumbnailAssetId?: string | null; message?: string }) =>
    api<PresetSummaryDto>('POST', '/api/presets', body),
  newPresetVersion: (id: string, content: PresetContent, message?: string, thumbnailAssetId?: string | null) =>
    api<PresetSummaryDto>('POST', `/api/presets/${id}/versions`, { content, message, thumbnailAssetId }),
  copyPreset: (id: string, scope: 'workspace' | 'private', name?: string) => api<PresetSummaryDto>('POST', `/api/presets/${id}/copy`, { scope, name }),
  patchPreset: (id: string, patch: { name?: string; category?: string; description?: string; tags?: string[] }) => api<PresetSummaryDto>('PATCH', `/api/presets/${id}`, patch),
  archivePreset: (id: string) => api('DELETE', `/api/presets/${id}`),
  uploadAsset: async (blob: Blob, filename: string): Promise<{ id: string }> => {
    const fd = new FormData();
    fd.append('file', blob, filename);
    const res = await fetch('/api/assets', { method: 'POST', body: fd, credentials: 'same-origin', headers: csrfToken ? { 'x-csrf-token': csrfToken } : {} });
    const json = (await res.json()) as Record<string, unknown>;
    if (!res.ok) throw new ApiError(res.status, String(json.error ?? 'error'), String(json.message ?? res.statusText), json);
    return json as { id: string };
  },
};
